import React, { useState, useEffect } from 'react';
import { Calendar, Phone, Mail, Clock, User, FileText, TrendingUp, Eye, LogOut, Trash2 } from 'lucide-react';
import axios from 'axios';
import AdminLogin from '../components/AdminLogin';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Check if user is authenticated
    const authenticated = localStorage.getItem('adminAuthenticated') === 'true';
    setIsAuthenticated(authenticated);
    
    if (authenticated) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    setLoading(true);
    fetchData();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, contactsRes, analyticsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bookings`),
        axios.get(`${BACKEND_URL}/api/contact`),
        axios.get(`${BACKEND_URL}/api/analytics/summary`)
      ]);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/bookings/${bookingId}/status?status=${newStatus}`);
      fetchData();
      toast.success('Booking status updated');
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast.error('Failed to update booking status');
    }
  };

  const deleteBooking = async (bookingId, patientName) => {
    if (!window.confirm(`Are you sure you want to delete the booking for "${patientName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/bookings/${bookingId}`);
      fetchData();
      toast.success('Booking deleted successfully');
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const markContactAsRead = async (contactId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${contactId}/read`);
      fetchData();
      toast.success('Message marked as read');
    } catch (error) {
      console.error('Error marking contact as read:', error);
      toast.error('Failed to mark message as read');
    }
  };

  const deleteContact = async (contactId, senderName) => {
    if (!window.confirm(`Are you sure you want to delete the message from "${senderName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/contact/${contactId}`);
      fetchData();
      toast.success('Message deleted successfully');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete message');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner"></div>
          <p className="mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      <div className="container py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="heading-1">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="btn-secondary flex items-center gap-2"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>

        {/* Analytics Overview */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="bg-card p-6 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Eye className="text-medical-red" size={24} />
                <h3 className="heading-3">Total Visits</h3>
              </div>
              <p className="text-3xl font-bold text-medical-red">{analytics.total_visits}</p>
            </div>
            
            <div className="bg-card p-6 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="text-medical-red" size={24} />
                <h3 className="heading-3">Bookings</h3>
              </div>
              <p className="text-3xl font-bold text-medical-red">{analytics.total_bookings}</p>
              <p className="body-small text-text-muted">{analytics.pending_bookings} pending</p>
            </div>

            <div className="bg-card p-6 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Mail className="text-medical-red" size={24} />
                <h3 className="heading-3">Messages</h3>
              </div>
              <p className="text-3xl font-bold text-medical-red">{analytics.total_contacts}</p>
              <p className="body-small text-text-muted">{analytics.unread_contacts} unread</p>
            </div>

            <div className="bg-card p-6 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="text-medical-red" size={24} />
                <h3 className="heading-3">Conversion</h3>
              </div>
              <p className="text-3xl font-bold text-medical-red">
                {analytics.total_visits > 0 
                  ? ((analytics.total_bookings / analytics.total_visits) * 100).toFixed(1)
                  : 0}%
              </p>
              <p className="body-small text-text-muted">Booking rate</p>
            </div>

            <div className="bg-card p-6 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="text-medical-red" size={24} />
                <h3 className="heading-3">Engagement</h3>
              </div>
              <p className="text-3xl font-bold text-medical-red">
                {analytics.total_visits > 0 
                  ? ((analytics.total_contacts / analytics.total_visits) * 100).toFixed(1)
                  : 0}%
              </p>
              <p className="body-small text-text-muted">Contact rate</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-border-light">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'overview'
                ? 'border-b-2 border-medical-red text-medical-red'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'bookings'
                ? 'border-b-2 border-medical-red text-medical-red'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'contacts'
                ? 'border-b-2 border-medical-red text-medical-red'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Contact Messages ({contacts.filter(c => !c.read).length} unread)
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div className="space-y-6">
            <div className="bg-card p-6 rounded-lg shadow-sm">
              <h2 className="heading-2 mb-4">Website Analytics</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="body-medium">Total Website Visits</span>
                  <span className="heading-3 text-medical-red">{analytics.total_visits}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="body-medium">Total Bookings</span>
                  <span className="heading-3">{analytics.total_bookings}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="body-medium">Total Contact Messages</span>
                  <span className="heading-3">{analytics.total_contacts}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t">
                  <span className="body-medium font-medium">Booking Conversion Rate</span>
                  <span className="heading-3 text-medical-red">
                    {analytics.total_visits > 0 
                      ? ((analytics.total_bookings / analytics.total_visits) * 100).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-sm">
                <h3 className="heading-3 mb-4">Recent Bookings</h3>
                {bookings.slice(0, 3).map((booking) => (
                  <div key={booking.id} className="mb-3 pb-3 border-b last:border-b-0">
                    <p className="body-medium font-medium">{booking.name}</p>
                    <p className="body-small text-text-muted">{booking.service_type} - {booking.date}</p>
                  </div>
                ))}
              </div>

              <div className="bg-card p-6 rounded-lg shadow-sm">
                <h3 className="heading-3 mb-4">Recent Messages</h3>
                {contacts.slice(0, 3).map((contact) => (
                  <div key={contact.id} className="mb-3 pb-3 border-b last:border-b-0">
                    <p className="body-medium font-medium">{contact.name}</p>
                    <p className="body-small text-text-muted">{contact.subject}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <div className="bg-card p-8 rounded-lg text-center">
                <p className="text-text-muted">No bookings yet</p>
              </div>
            ) : (
              bookings.map((booking) => (
                <div key={booking.id} className="bg-card p-6 rounded-lg shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="heading-3 flex items-center gap-2">
                        <User size={20} />
                        {booking.name}
                      </h3>
                      <p className="body-small text-text-muted">
                        {new Date(booking.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={booking.status}
                        onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                        className="px-3 py-1 border rounded text-sm"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button
                        onClick={() => deleteBooking(booking.id, booking.name)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded transition"
                        title="Delete booking"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-medical-red" />
                      <span className="body-medium">{booking.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-medical-red" />
                      <span className="body-medium">{booking.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-medical-red" />
                      <span className="body-medium">{booking.date} at {booking.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-medical-red" />
                      <span className="body-medium">{booking.service_type}</span>
                    </div>
                  </div>

                  {booking.message && (
                    <div className="mt-4 p-3 bg-section rounded">
                      <p className="body-small font-medium mb-1">Patient Message:</p>
                      <p className="body-medium">{booking.message}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <div className="bg-card p-8 rounded-lg text-center">
                <p className="text-text-muted">No contact messages yet</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`bg-card p-6 rounded-lg shadow-sm ${
                    !contact.read ? 'border-l-4 border-medical-red' : ''
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="heading-3 flex items-center gap-2">
                        <User size={20} />
                        {contact.name}
                        {!contact.read && (
                          <span className="px-2 py-1 bg-medical-red text-white text-xs rounded">
                            New
                          </span>
                        )}
                      </h3>
                      <p className="body-small text-text-muted">
                        {new Date(contact.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!contact.read && (
                        <button
                          onClick={() => markContactAsRead(contact.id)}
                          className="btn-secondary text-sm"
                        >
                          Mark as Read
                        </button>
                      )}
                      <button
                        onClick={() => deleteContact(contact.id, contact.name)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded transition"
                        title="Delete message"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-medical-red" />
                      <span className="body-medium">{contact.email}</span>
                    </div>
                    {contact.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={16} className="text-medical-red" />
                        <span className="body-medium">{contact.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-medical-red" />
                      <span className="body-medium font-medium">{contact.subject}</span>
                    </div>
                  </div>

                  <div className="p-3 bg-section rounded">
                    <p className="body-medium whitespace-pre-wrap">{contact.message}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
